import { useMemo } from 'react';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteTwoTone';
import { getApiErrorMessage } from '@api/getApiError';
import { Document } from '@api/Trinium/Endpoints/Document';
import { usePutDocumentsMutation } from '@api/Trinium/Endpoints/Document/slice';
import { useModalProvider } from '@components/Modals/ModalContext';
import { emptyRowSet } from '@components/DataGrid/types';
import useCurrentTerminal from '@hooks/useCurrentTerminal';

import styles from '../DataGrid.DocumentControl.module.css';

/**
 * Delete Document
 */
export default function DeleteAction() {
	const { number: terminalNumber } = useCurrentTerminal();
	const modalContext = useModalProvider();
	const { queueModalAnnouncement } = modalContext;
	const [putDocuments, { isLoading: isDeleting }] = usePutDocumentsMutation();
	const gridApiRef = useGridApiContext();
	const selectedRowsMap = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	const selectedRows = useMemo<Document[]>(() => Array.from(selectedRowsMap.values()) as Document[], [selectedRowsMap]);

	const selectedDocumentIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);

	const handleBtnClick = async () => {
		await putDocuments({
			terminalNumber,
			actionType: {
				action: 'deleteDocuments',
				data: {
					docIds: selectedDocumentIds
				}
			}
		})
			.unwrap()
			.then(() => {
				gridApiRef.current.setRowSelectionModel(emptyRowSet);
			})
			.catch((ex: unknown) => {
				queueModalAnnouncement({
					severity: 'error',
					message: getApiErrorMessage(ex)
				});
			});
	};

	return (
		<IconButton
			title='Delete Document'
			onClick={() => {
				void handleBtnClick();
			}}
			loading={isDeleting}
			disabled={!selectedDocumentIds.length || isDeleting}
			className={styles['icon-btn']}
		>
			<DeleteIcon fontSize='small' />
		</IconButton>
	);
}
